/**
 * Talbeena / Nutri Nuts / Care pricing — amounts shown as Indian rupees (₹).
 * Shopify APIs still use paise internally; this module converts for display & cart math.
 */
window.MukhlisPricing = (() => {
  /** Add-on MRP in Shopify paise (₹300) */
  const ADDON_MRP = 30000;

  const ADDON_PRICES = {
    takmeeli: { 2: 22000, 3: 20000, 4: 19000 },
    nutrinuts: { 2: 23000, 3: 21000, 4: 19000, 5: 17000, 6: 15000 },
  };

  /** Format Shopify paise → Indian rupees (₹1,199) */
  function money(paise) {
    const rupees = Math.round(Number(paise) / 100) || 0;
    try {
      return `₹${rupees.toLocaleString('en-IN')}`;
    } catch {
      return `₹${rupees}`;
    }
  }

  /** Format rupee amount → ₹ */
  function moneyRupees(rupees) {
    const n = Math.round(Number(rupees)) || 0;
    try {
      return `₹${n.toLocaleString('en-IN')}`;
    } catch {
      return `₹${n}`;
    }
  }

  function toPaise(rupees) {
    return Math.round(Number(rupees) * 100) || 0;
  }

  /** Parse "₹300", "Rs. 220.00", "1,199" → paise */
  function parseRupeesToPaise(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'number' && Number.isFinite(value)) {
      // Already paise if very large; otherwise treat as rupees
      if (value >= 1000 && Number.isInteger(value) && value % 100 === 0 && value >= 10000) {
        return Math.round(value);
      }
      return Math.round(value * 100);
    }
    const raw = String(value).replace(/[^\d.]/g, '');
    if (!raw) return 0;
    return Math.round(Number(raw) * 100);
  }

  function addonOfferForCombo(family, comboSize) {
    const table = ADDON_PRICES[family] || ADDON_PRICES.takmeeli;
    const size = Number(comboSize);
    return table[size] || table[2] || 0;
  }

  /** Same formula as PDP — inputs in Shopify paise */
  function orderSavings({ comboPrice, comboCompare, qty = 1, addonOn, addonOffer, addonMrp }) {
    const q = Math.max(1, Number(qty) || 1);
    const sale = Number(comboPrice) * q + (addonOn ? Number(addonOffer) : 0);
    const comboCmp = Number(comboCompare) > Number(comboPrice) ? Number(comboCompare) : 0;
    const compare = comboCmp * q + (addonOn ? Number(addonMrp) : 0);
    return Math.max(0, compare - sale);
  }

  function orderTotal({ comboPrice, qty = 1, addonOn, addonOffer }) {
    const q = Math.max(1, Number(qty) || 1);
    return Number(comboPrice) * q + (addonOn ? Number(addonOffer) : 0);
  }

  /** Max save = combo MRP cut + optional add-on MRP cut (no prepaid offer on Talbeena / Nutri Nuts) */
  function saveUpToPaise({
    price,
    compareAt,
    includeAddon = false,
    family,
    comboSize,
    addonOffer,
    addonMrp,
    qty = 1,
  }) {
    const q = Math.max(1, Number(qty) || 1);
    const unitSale = Math.max(0, Number(price) || 0);
    const unitCmp = Math.max(0, Number(compareAt) || 0);
    const mrpSave = (unitCmp > unitSale ? unitCmp - unitSale : 0) * q;

    let addonSale = 0;
    let addonSave = 0;
    if (includeAddon) {
      const offer =
        addonOffer != null && addonOffer !== ''
          ? Number(addonOffer)
          : addonOfferForCombo(family, comboSize);
      const mrp = addonMrp != null && addonMrp !== '' ? Number(addonMrp) : ADDON_MRP;
      if (offer > 0) {
        // One add-on per combo unit (matches cart rules)
        addonSale = offer * q;
        addonSave = (mrp > offer ? mrp - offer : 0) * q;
      }
    }

    return Math.max(0, mrpSave + addonSave);
  }

  function parseSnapshot(properties) {
    if (!properties) return null;
    // New format: _order stores Indian rupees
    if (properties._order) {
      try {
        const o = JSON.parse(properties._order);
        return {
          comboPrice: toPaise(o.price),
          comboCompare: toPaise(o.mrp),
          qty: Number(o.qty) || 1,
          addonOn: Boolean(o.addon),
          addonOffer: toPaise(o.addonPrice),
          addonMrp: toPaise(o.addonMrp),
        };
      } catch {
        /* fall through */
      }
    }
    // Legacy snapshot (paise fields) — still supported for old cart lines
    if (properties._pdp_snapshot) {
      try {
        return JSON.parse(properties._pdp_snapshot);
      } catch {
        return null;
      }
    }
    return null;
  }

  function isAddonLine(properties) {
    if (!properties) return false;
    return Boolean(properties['Linked combo'] || properties._addon === '1');
  }

  function pricesFromProps(properties) {
    if (!properties?._prices) return null;
    try {
      return JSON.parse(properties._prices);
    } catch {
      return null;
    }
  }

  function offerPaiseFromProps(properties) {
    if (!properties) return 0;
    const packed = pricesFromProps(properties);
    if (packed?.offer > 0) return toPaise(packed.offer);
    // Legacy cart lines
    return parseRupeesToPaise(properties['Offer price']);
  }

  function mrpPaiseFromProps(properties) {
    if (!properties) return 0;
    const packed = pricesFromProps(properties);
    if (packed?.mrp > 0) return toPaise(packed.mrp);
    const fromLabel = parseRupeesToPaise(properties.MRP);
    if (fromLabel > 0) return fromLabel;
    if (isAddonLine(properties)) return ADDON_MRP;
    return 0;
  }

  function lineDisplayPricing(item) {
    const qty = Math.max(1, Number(item.quantity) || 1);
    const props = item.properties || {};
    const isAddon = isAddonLine(props);

    let sale = Number(item.final_line_price) || Number(item.line_price) || 0;
    const offerUnit = offerPaiseFromProps(props);
    if (isAddon && offerUnit > 0) {
      sale = offerUnit * qty;
    }

    let compare = 0;

    if (isAddon) {
      const mrpUnit = mrpPaiseFromProps(props) || ADDON_MRP;
      compare = mrpUnit * qty;
    } else {
      if (Number(item.original_line_price) > sale) {
        compare = Number(item.original_line_price);
      }
      const unitCompare = Number(item.original_price) || 0;
      const unitPrice = Number(item.price) || 0;
      if (unitCompare > unitPrice) {
        compare = Math.max(compare, unitCompare * qty);
      }
      const fromMrp = mrpPaiseFromProps(props);
      if (fromMrp > 0) {
        compare = Math.max(compare, fromMrp * qty);
      }
    }

    return {
      sale,
      compare: compare > sale ? compare : 0,
      savings: compare > sale ? compare - sale : 0,
      isAddon,
    };
  }

  function cartSavings(items) {
    return cartTotals(items).savings;
  }

  function cartTotals(items) {
    const list = items || [];
    const snapshots = list.map((item) => parseSnapshot(item.properties)).filter(Boolean);

    if (snapshots.length) {
      let sale = 0;
      let compare = 0;
      snapshots.forEach((s) => {
        const q = Math.max(1, Number(s.qty) || 1);
        const comboPrice = Number(s.comboPrice) || 0;
        const comboCmp = Number(s.comboCompare) > comboPrice ? Number(s.comboCompare) : 0;
        sale += comboPrice * q + (s.addonOn ? Number(s.addonOffer) || 0 : 0);
        compare += comboCmp * q + (s.addonOn ? Number(s.addonMrp) || 0 : 0);
      });

      const snapKeys = new Set(
        list.filter((item) => parseSnapshot(item.properties)).map((item) => item.key)
      );
      list.forEach((item) => {
        if (snapKeys.has(item.key)) return;
        if (isAddonLine(item.properties) && snapshots.some((s) => s.addonOn)) return;
        const p = lineDisplayPricing(item);
        sale += p.sale;
        compare += p.compare || p.sale;
      });

      const savings = Math.max(0, compare - sale);
      return { sale, compare: compare > sale ? compare : 0, savings };
    }

    let sale = 0;
    let compare = 0;
    list.forEach((item) => {
      const p = lineDisplayPricing(item);
      sale += p.sale;
      compare += p.compare > p.sale ? p.compare : p.sale;
    });
    const savings = Math.max(0, compare - sale);
    return { sale, compare: compare > sale ? compare : 0, savings };
  }

  function linePriceHtml(item) {
    const p = lineDisplayPricing(item);
    if (p.compare > p.sale) {
      return `<span class="cart-line-price"><s class="cart-line-price__mrp" aria-label="MRP">${money(p.compare)}</s> <strong class="cart-line-price__sale">${money(p.sale)}</strong></span>`;
    }
    return `<strong class="cart-line-price__sale">${money(p.sale)}</strong>`;
  }

  function sortedCartProperties(properties) {
    if (!properties || typeof properties !== 'object') return [];
    return Object.entries(properties)
      .filter(([key, value]) => {
        if (!value && value !== 0) return false;
        if (String(key).startsWith('_')) return false;
        if (/cent/i.test(String(key))) return false;
        if (key === 'Offer price' || key === 'MRP' || key === 'Addon') return false;
        return true;
      })
      .sort((a, b) => {
        const rank = (key) => {
          if (key === 'Combo') return 0;
          if (key === 'Set') return 1;
          if (key === 'Flavours') return 2;
          if (key === 'Flavour') return 3;
          if (key === 'Linked combo') return 4;
          const m = String(key).match(/^Flavour\s+(\d+)$/i);
          return m ? 10 + Number(m[1]) : 100;
        };
        return rank(a[0]) - rank(b[0]) || String(a[0]).localeCompare(String(b[0]));
      });
  }

  function propertiesHtml(properties) {
    const entries = sortedCartProperties(properties);
    if (!entries.length) return '';
    return `<ul class="cart-props">${entries
      .map(([key, value]) => {
        const label = key === 'Flavour' && isAddonLine(properties) ? 'Add-on flavour' : key;
        return `<li><span>${label}:</span> ${value}</li>`;
      })
      .join('')}</ul>`;
  }

  const PREPAID_NOTE = 'Pay prepaid at checkout for 10% extra off your order.';

  return {
    ADDON_MRP,
    ADDON_PRICES,
    PREPAID_NOTE,
    money,
    moneyRupees,
    addonOfferForCombo,
    orderSavings,
    orderTotal,
    saveUpToPaise,
    isAddonLine,
    lineDisplayPricing,
    cartSavings,
    cartTotals,
    linePriceHtml,
    propertiesHtml,
  };
})();
